import { useEffect, useState } from "react";

export default function useProjectFacilitatorOverview({ apiUrl, slug, token, readShape = "overview" }) {
  const [state, setState] = useState({ overview: null, evidenceByProgram: {}, loading: true, error: "" });

  useEffect(() => {
    let current = true;
    const load = async () => {
      if (!token || !slug) {
        if (current) setState({ overview: null, evidenceByProgram: {}, loading: false, error: "Project overview is unavailable." });
        return;
      }
      try {
        setState({ overview: null, evidenceByProgram: {}, loading: true, error: "" });
        const headers = { Authorization: `Bearer ${token}` };
        const discoveryResponse = await fetch(`${apiUrl}/projects/facilitator`, { headers });
        if (!discoveryResponse.ok) throw new Error("OVERVIEW_UNAVAILABLE");
        const discovery = await discoveryResponse.json();
        const project = (discovery?.projects || []).find((item) => item.slug === slug);
        if (!project) throw new Error("PROJECT_NOT_AUTHORIZED");
        const [overviewResponse, rosterResponse, progressResponse] = await Promise.all([
          fetch(`${apiUrl}/projects/${project.id}/facilitator/overview`, { headers }),
          fetch(`${apiUrl}/projects/${project.id}/facilitator/roster`, { headers }),
          fetch(`${apiUrl}/projects/${project.id}/facilitator/program-progress`, { headers }),
        ]);
        if (!overviewResponse.ok || !rosterResponse.ok || !progressResponse.ok) {
          throw new Error("OVERVIEW_UNAVAILABLE");
        }
        const [overview, roster, progress] = await Promise.all([
          overviewResponse.json(), rosterResponse.json(), progressResponse.json(),
        ]);
        const entries = await Promise.all((overview.programs || []).map(async program => {
          const query = new URLSearchParams({ programSlug: program.slug, programVersion: program.version, readShape: readShape === "cohort" ? "cohort" : "summary" });
          const response = await fetch(`${apiUrl}/projects/${project.id}/facilitator/program-evidence?${query}`, { headers });
          return [program.id, response.ok ? await response.json() : null];
        }));
        const progressByParticipant = new Map((progress.participants || []).map(item => [item.participantRef, item]));
        const participants = readShape === "cohort" ? (roster.members || []).map(member => ({
          ...member,
          programs: progressByParticipant.get(member.participantRef)?.programs || [],
          evidenceByProgram: {},
        })) : await Promise.all((roster.members || []).map(async member => {
          const evidenceEntries = await Promise.all((overview.programs || []).map(async program => {
            const query = new URLSearchParams({
              programSlug: program.slug,
              programVersion: program.version,
              membershipId: member.membershipId,
              readShape: "summary",
            });
            const response = await fetch(`${apiUrl}/projects/${project.id}/facilitator/program-evidence?${query}`, { headers });
            return [program.id, response.ok ? await response.json() : null];
          }));
          return {
            ...member,
            programs: progressByParticipant.get(member.participantRef)?.programs || [],
            evidenceByProgram: Object.fromEntries(evidenceEntries),
          };
        }));
        if (current) setState({
          overview: { ...overview, participants },
          evidenceByProgram: Object.fromEntries(entries),
          loading: false,
          error: "",
        });
      } catch (requestError) {
        if (current) setState({
          overview: null, evidenceByProgram: {},
          loading: false,
          error: requestError.message === "PROJECT_NOT_AUTHORIZED"
            ? "This Project is not available for facilitation."
            : "Project overview is temporarily unavailable.",
        });
      }
    };
    load();
    return () => { current = false; };
  }, [apiUrl, readShape, slug, token]);

  return state;
}
